import { useEditor } from "features/editor/Feature";
import { ShellProvider } from "features/shell/Feature";
import type { Step } from "features/types/setupProgress";
import type { Tab } from "features/types/tabs";
import { usePitcher } from "features/utils/usePitcher";
import { useCapabilities } from "hooks/pitcher/useCapabilities";
import { usePermissions } from "hooks/pitcher/usePermissions";
import { useSetupProgress } from "hooks/pitcher/useSetupProgress";
import { useTasks } from "hooks/pitcher/useTasks";
import { Box, Button, Icon, Stack, styled, Text } from "prism-react";
import type { IconType, Tokens } from "prism-react";
import { useEffect, useState } from "react";

import { useAnalytics } from "hooks/useAnalytics";
import { useEditorRouter } from "hooks/useEditorRouter";
import { executeVSCodeCommand } from "utils/vscode";

import { Shell } from "../Shell/Shell";

export const SetupTasks: React.FC<{ tab: Tab }> = () => {
  const tasks = useTasks();
  const setupProgress = useSetupProgress();
  const editor = useEditor();
  const track = useAnalytics();
  const pitcher = usePitcher();
  const pitcherCapabilities = useCapabilities();
  const pitcherPermissions = usePermissions();
  const router = useEditorRouter();

  const handleSetupFinished = () => {
    // If setup was the last step, open the starting devtools
    const defaultRunningTasks = Object.values(tasks).filter(
      (t) => t.runAtStart,
    );

    defaultRunningTasks.forEach((task) => {
      executeVSCodeCommand("codesandbox.terminal.open", { taskId: task.id });
    });

    // Close the setup as soon as the new devtools are added
    setTimeout(() => {
      executeVSCodeCommand("codesandbox.layout.closeDevtool", {
        type: "SETUP_TASKS",
      });
    });

    track("setup_tasks_completed", {
      runningTasksCount: defaultRunningTasks.length,
      import: !!router.branch.queryParams.import.get(),
      type: editor.type === "branch" ? "branch" : "devbox",
      protectBranch: editor.type === "branch" ? editor.protectedBranch : false,
    });
  };

  useEffect(() => {
    if (setupProgress.state === "FINISHED") {
      handleSetupFinished();

      router.branch.queryParams.import.delete();
    }
  }, [setupProgress]);

  useEffect(() => {
    track("setup_tasks_started", {
      import: !!router.branch.queryParams.import.get(),
      type: editor.type === "branch" ? "branch" : "devbox",
      protectBranch: editor.type === "branch" ? editor.protectedBranch : false,
    });
  }, []);

  const title = router.queryParams.migrateFrom.get()
    ? "Devbox setup"
    : "Running setup tasks";

  return (
    <Stack
      css={{
        paddingTop: "$8",
        height: "100%",
        width: "100%",
        maxWidth: "max(750px, 80%)",
        margin: "auto",
        alignItems: "center",
      }}
      gap={8}
    >
      <Stack css={{ textAlign: "center" }} gap={4}>
        <Text css={{ color: "$neutral-fg-high" }} size="md">
          {title}
        </Text>

        <Text
          as="p"
          color="neutral-fg-base"
          css={{ maxWidth: "400px", textWrap: "balance" }}
          size="sm"
        >
          This initial setup may take a while, but subsequent starts will be
          instant.
        </Text>
      </Stack>
      <Stack css={{ width: "100%" }} gap={1}>
        {setupProgress.steps.map((step, index) => {
          const isCurrentStep = index === setupProgress.currentStepIndex;

          const stepWaiting = index > setupProgress.currentStepIndex;
          return (
            <ExpandableLogsButton
              key={index}
              isAllowedToRestart={
                !!(
                  pitcherCapabilities.setup?.setStep &&
                  pitcherPermissions.setup?.setStep
                )
              }
              isCurrentStep={isCurrentStep}
              onRestartStep={() => pitcher.clients.setup.setStep(index)}
              onSkipStep={() => pitcher.clients.setup.skipStep(index)}
              step={step}
              stepWaiting={stepWaiting}
            />
          );
        })}
      </Stack>
      {!!(
        pitcherCapabilities.setup?.skipAll && pitcherPermissions.setup?.skipAll
      ) && (
        <Box css={{ alignSelf: "center" }}>
          <Button
            onClick={() => {
              pitcher.clients.setup.skipAll();
              handleSetupFinished();
            }}
            variant="ghost"
          >
            Skip all
          </Button>
        </Box>
      )}
    </Stack>
  );
};

type ExpandableLogsButtonProps = {
  step: Step;
  isCurrentStep: boolean;
  isAllowedToRestart: boolean;
  stepWaiting: boolean;
  onRestartStep: () => void;
  onSkipStep: () => void;
};

const ExpandableLogsButton: React.FC<ExpandableLogsButtonProps> = ({
  step,
  stepWaiting,
  isAllowedToRestart,
  isCurrentStep,
  onRestartStep,
  onSkipStep,
}) => {
  const editor = useEditor();
  const router = useEditorRouter();
  const track = useAnalytics();
  const stepSkipped = step.finishStatus === "SKIPPED";
  const [manuallyExpanded, setManuallyExpanded] = useState<boolean | null>(
    null,
  );

  const [numberOfRestarts, setNumberOfRestarts] = useState(0);

  const handleError = () => {
    /**
     * After restarting the branch, some setup tasks fail for unknown reasons
     * so we need to retry to connect to a new shell by restarting the step
     */
    if (numberOfRestarts < 2) {
      setNumberOfRestarts((prev) => prev + 1);
      onRestartStep();
    }
  };

  const [autoExpanded, setAutoExpanded] = useState<boolean>(false);

  const isExpanded = manuallyExpanded ?? autoExpanded;

  useEffect(() => {
    if (!isCurrentStep) {
      return;
    }

    // If steps are done in less then 200 ms, it's not worth showing the logs
    const cleanupTimer = setTimeout(() => {
      setAutoExpanded(true);
    }, 200);

    return () => {
      setAutoExpanded(false);
      setManuallyExpanded(null);

      clearTimeout(cleanupTimer);
    };
  }, [isCurrentStep]);

  useEffect(() => {
    if (step.finishStatus === "FAILED") {
      track("setup_task_failed", {
        name: step.name,
        import: !!router.branch.queryParams.import.get(),
        type: editor.type === "branch" ? "branch" : "devbox",
        protectBranch:
          editor.type === "branch" ? editor.protectedBranch : false,
      });
    }
  }, [step.finishStatus]);

  return (
    <Box>
      <StyledButton
        disabled={stepWaiting}
        onClick={() => setManuallyExpanded(!isExpanded)}
      >
        <Stack gap={3} horizontal>
          <Icon
            css={{
              color: "$neutral-fg-base",
              transition: "rotate $fast",
              rotate: isExpanded ? "90deg" : "0",
            }}
            size={3}
            type="caret"
          />
          <Icon
            css={{ color: `$${stepColor(step)}` }}
            size={4}
            type={stepIcon(step, isCurrentStep)}
          />
          <Text
            css={{
              flex: 1,
              transition: "color $fast",
              color: isCurrentStep ? "$neutral-fg-subtle" : "$neutral-fg-base",
              textDecoration: stepSkipped ? "line-through" : "none",
            }}
          >
            {step.name}
          </Text>

          {isExpanded && isAllowedToRestart && (
            <Stack gap={1} horizontal>
              <Button
                icon="refresh"
                onClick={(event) => {
                  event.stopPropagation();
                  onRestartStep();
                }}
              >
                Restart
              </Button>

              {isCurrentStep && (
                <Button
                  icon="enter"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSkipStep();
                  }}
                >
                  Skip
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </StyledButton>
      {step.shellId && (
        <WrapperShell isExpanded={isExpanded}>
          <ShellProvider key={step.shellId} shellId={step.shellId}>
            <Shell
              onOpenError={handleError}
              overrideBackgroundColor="neutral-bg-medium"
              variant="command"
            />
          </ShellProvider>
        </WrapperShell>
      )}
    </Box>
  );
};

const WrapperShell = styled("div", {
  transition: "height $slow, opacity $fast",
  height: 0,
  opacity: 0,
  width: "100%",
  background: "$neutral-bg-medium",
  overflow: "hidden",
  borderRadius: "$2",

  variants: {
    isExpanded: {
      true: {
        height: 400,
        opacity: 1,
        padding: "$4",
      },
    },
  },
});

const stepColor = (step: Step): keyof Tokens["colors"] => {
  if (!step.finishStatus) {
    return "neutral-fg-base";
  }

  switch (step.finishStatus) {
    case "SUCCEEDED":
      return "informative-success-base";
    case "FAILED":
      return "informative-error-base";
    case "SKIPPED":
    default:
      return "neutral-fg-base";
  }
};

const stepIcon = (step: Step, isCurrentStep: boolean): IconType => {
  if (!step.finishStatus) {
    return isCurrentStep ? "progress" : "circle";
  }

  switch (step.finishStatus) {
    case "SUCCEEDED":
      return "check";
    case "FAILED":
      return "circleBang";
    case "SKIPPED":
      return "circle";
    default:
      return "ghost";
  }
};

const StyledButton = styled("button", {
  display: "block",
  textAlign: "left",
  position: "relative",
  height: "$9",
  width: "100%",

  [`&:hover:not([disabled]) ${Text}`]: {
    color: "$neutral-fg-high",
  },
});
